import { Dim2 } from ".";

export type Hex = string;

export type RGB =
{
    r: number
    g: number
    b: number
}

export type HSV =
{
    h: number;
    s: number;
    v: number;
}

export class Color
{
    hex: Hex;
    hsv: HSV;
    rgb: RGB;

    constructor(hex: Hex, hsv: HSV, rgb: RGB)
    {
        this.hex = hex;
        this.hsv = hsv;
        this.rgb = rgb;
    }
}

export function validateHex(value: string, errorValue?: Hex): Hex
{
    if (!value.startsWith('#'))
    {
        const ctx = document.createElement('canvas').getContext('2d');
        if (!ctx)
        {
            if (errorValue) { return errorValue; }
            throw new Error('2d context not supported or canvas already initialized');
        }
        ctx.fillStyle = value;
        return ctx.fillStyle;
    }

    if (value.length === 4 || value.length === 5)
    {
        return value.split('').map((v, i) => (i ? v + v : '#')).join('');
    }

    if (value.length === 7 || value.length === 9)
    {
        return value;
    }

    return '#000000';
}

export function hex2rgb(hex: string): RGB
{
    const matches = hex
        .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (_, r, g, b) => '#' + r + r + g + g + b + b)
        .substring(1)
        .match(/.{2}/g) || [];
    const rgb = matches.map((x) => parseInt(x, 16));
    return { r: rgb[0], g: rgb[1], b: rgb[2] };
}

export function rgb2hsv(rgb: RGB): HSV
{
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;
    const max = Math.max(r, g, b);
    const d = max - Math.min(r, g, b);
    const h = d
        ? (max === r
            ? (g - b) / d + (g < b ? 6 : 0)
            : max === g
            ? 2 + (b - r) / d
            : 4 + (r - g) / d) * 60
        : 0;
    const s = max ? (d / max) : 0;
    const v = max;
    return { h, s, v };
}

export function hsv2rgb(hsv: HSV): RGB
{
    const { h, s, v } = hsv;
    const i = ~~(h / 60);
    const f = h / 60 - i;
    const p = v * (1 - s);
    const q = v * (1 - s * f);
    const t = v * (1 - s * (1 - f));
    const index = i % 6;
    const r = Math.round([v, q, p, p, t, v][index] * 255);
    const g = Math.round([t, v, v, q, p, p][index] * 255);
    const b = Math.round([p, p, t, v, v, q][index] * 255);
    return {r, g, b};
}

export function rgb2hex({ r, g, b }: RGB): string
{
    return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

export function createColor<F extends keyof Color, T extends Color[F]>(format: F, value: T): Color
{
    let hex, rgb, hsv;
    switch (format)
    {
        case 'hex':
            hex = validateHex(value as string);
            rgb = hex2rgb(hex);
            hsv = rgb2hsv(rgb);
            if (hsv.s > 1)
            {
                // eslint-disable-next-line no-debugger
                debugger;
            }
            return { hex, hsv, rgb };
        case 'hsv':
            hsv = value as HSV;
            rgb = hsv2rgb(hsv);
            hex = rgb2hex(rgb);
            if (hsv.s > 1)
            {
                // eslint-disable-next-line no-debugger
                debugger;
            }
            return { hex, hsv, rgb };
        case 'rgb':
            rgb = value as RGB;
            hex = rgb2hex(rgb);
            hsv = rgb2hsv(rgb);
            if (hsv.s > 1)
            {
                // eslint-disable-next-line no-debugger
                debugger;
            }
            return { hex, hsv, rgb };
        default:
            throw new Error();
    }
}

export function saturationToPoint(value: Color, width: number, height: number): Dim2.Point
{
    const x = value.hsv.s * width;
    const y = (1 - value.hsv.v) * height;
    return { x, y };
}